import { call, put, takeEvery, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { eventTypeApi, EventType, EventTypeSearchParams } from '../../services/eventTypeApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setStatistics,
  setBulkOperationLoading,
  setSuccessMessage,
  FETCH_EVENT_TYPES,
  FETCH_EVENT_TYPE_BY_ID,
  CREATE_EVENT_TYPE,
  UPDATE_EVENT_TYPE,
  DELETE_EVENT_TYPE,
  SEARCH_EVENT_TYPES,
  FETCH_STATISTICS,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/eventTypeSlice';

// Fetch all Event Types
function* fetchEventTypesSaga(action: PayloadAction<EventTypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { pagination, searchParams } = state.eventType;
    
    const params = {
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: searchParams.search || '',
      active: searchParams.active,
      sortBy: searchParams.sortBy || 'Name',
      sortOrder: searchParams.sortOrder || 'asc',
      ...action.payload,
    };
    
    const response = yield call(eventTypeApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching Event Types:', error);
    yield put(setError(error.message || 'Failed to fetch Event Types'));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch Event Type by ID
function* fetchEventTypeByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const eventType = yield call(eventTypeApi.getById, action.payload);
    yield put(setSelectedItem(eventType));
  } catch (error: any) {
    console.error('Error fetching Event Type by ID:', error);
    yield put(setError(error.message || 'Failed to fetch Event Type'));
  } finally {
    yield put(setLoading(false));
  }
}

// Create Event Type
function* createEventTypeSaga(action: PayloadAction<Omit<EventType, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(eventTypeApi.validation.validateEventType, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newEventType = yield call(eventTypeApi.create, action.payload);
    yield put(addItem(newEventType));
    yield put(setSuccessMessage('Event Type created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating Event Type:', error);
    yield put(setError(error.message || 'Failed to create Event Type'));
  } finally {
    yield put(setLoading(false));
  }
}

// Update Event Type
function* updateEventTypeSaga(action: PayloadAction<{ id: number; eventType: Partial<EventType> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, eventType } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(eventTypeApi.validation.validateEventType, eventType);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedEventType = yield call(eventTypeApi.update, id, eventType);
    yield put(updateItem(updatedEventType));
    yield put(setSuccessMessage('Event Type updated successfully'));
  } catch (error: any) {
    console.error('Error updating Event Type:', error);
    yield put(setError(error.message || 'Failed to update Event Type'));
  } finally {
    yield put(setLoading(false));
  }
}

// Delete Event Type
function* deleteEventTypeSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(eventTypeApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('Event Type deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete Event Type'));
    }
  } catch (error: any) {
    console.error('Error deleting Event Type:', error);
    yield put(setError(error.message || 'Failed to delete Event Type'));
  } finally {
    yield put(setLoading(false));
  }
}

// Search Event Types
function* searchEventTypesSaga(action: PayloadAction<EventTypeSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(eventTypeApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching Event Types:', error);
    yield put(setError(error.message || 'Failed to search Event Types'));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch statistics
function* fetchStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(eventTypeApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    // Don't set error for statistics as it's not critical
  }
}

// Bulk activate
function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(eventTypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(eventTypeApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Event Types activated successfully'));
      // Refresh the list
      yield put({ type: FETCH_EVENT_TYPES });
    } else {
      yield put(setError(response.message || 'Failed to activate Event Types'));
    }
  } catch (error: any) {
    console.error('Error bulk activating Event Types:', error);
    yield put(setError(error.message || 'Failed to activate Event Types'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk deactivate
function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(eventTypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(eventTypeApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Event Types deactivated successfully'));
      // Refresh the list
      yield put({ type: FETCH_EVENT_TYPES });
    } else {
      yield put(setError(response.message || 'Failed to deactivate Event Types'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating Event Types:', error);
    yield put(setError(error.message || 'Failed to deactivate Event Types'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk delete
function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(eventTypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(eventTypeApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Event Types deleted successfully'));
      // Refresh the list
      yield put({ type: FETCH_EVENT_TYPES });
    } else {
      yield put(setError(response.message || 'Failed to delete Event Types'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting Event Types:', error);
    yield put(setError(error.message || 'Failed to delete Event Types'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Export to CSV
function* exportToCSVSaga(action: PayloadAction<EventTypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { searchParams } = state.eventType;
    
    const params = {
      ...searchParams,
      ...action.payload,
    };
    
    const blob = yield call(eventTypeApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `eventtypes_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('Event Types exported successfully'));
  } catch (error: any) {
    console.error('Error exporting Event Types:', error);
    yield put(setError(error.message || 'Failed to export Event Types'));
  } finally {
    yield put(setLoading(false));
  }
}

// Root Event Type Saga
export function* eventTypeSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_EVENT_TYPES, fetchEventTypesSaga);
  yield takeEvery(FETCH_EVENT_TYPE_BY_ID, fetchEventTypeByIdSaga);
  yield takeEvery(CREATE_EVENT_TYPE, createEventTypeSaga);
  yield takeEvery(UPDATE_EVENT_TYPE, updateEventTypeSaga);
  yield takeEvery(DELETE_EVENT_TYPE, deleteEventTypeSaga);
  yield takeEvery(SEARCH_EVENT_TYPES, searchEventTypesSaga);
  yield takeEvery(FETCH_STATISTICS, fetchStatisticsSaga);
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
} 